import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listRecentTraces, type RunTraceSummary } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Traces index — recent `uaip run --trace` uploads, most-recent-first.
 *
 * This lives at /traces and backs the top-nav "Trace" link. Each row
 * drills into /runs/:id/trace, the viewer page. Keep the rendering
 * cheap (no JSON parsing here) — the API already hoists status /
 * framework / runner / duration_ms / test_count onto the row.
 *
 * We deliberately do NOT add a filter bar for v0. The dataset is
 * small enough (one trace per run) that ordering by upload time and
 * capping to 100 rows is plenty. If the user asks for filtering we
 * can crib the SegGroup pattern from RunsPage later.
 */
export function TracesPage(): JSX.Element {
  const { user, ready } = useAuth();
  const [traces, setTraces] = useState<RunTraceSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const rows = await listRecentTraces(100);
      setTraces(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    // Traces are scoped to the signed-in user — skip the fetch while we're
    // either still resolving the session or known-anonymous. The signed-out
    // CTA below renders the whole view in that case.
    if (!ready || !user) return;
    load();
  }, [ready, user, load]);

  if (ready && !user) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900 p-6 text-sm text-slate-300">
        <p className="font-medium text-slate-100">Sign in to manage traces</p>
        <p className="mt-1 text-slate-400">
          Traces are user-owned — the index only shows runs uploaded from
          your own <code className="rounded bg-slate-800 px-1 text-slate-200">uaip run --trace</code> invocations.
        </p>
        <Link
          to="/sign-in?redirect=/traces"
          className="mt-4 inline-block rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Traces</h1>
          <p className="mt-1 text-sm text-slate-400">
            Each time you run your generated tests with{" "}
            <code className="rounded bg-slate-800 px-1 text-slate-200">
              uaip run --trace &lt;dir&gt;
            </code>
            , the manifest is uploaded here. Click a row to open the trace
            viewer.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:border-slate-600 disabled:opacity-50"
        >
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-700 bg-rose-900/40 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {traces === null && !error && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
          Loading traces…
        </div>
      )}

      {traces !== null && traces.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
          <p className="text-slate-300">No traces yet.</p>
          <p className="mt-1 text-sm text-slate-500">
            Emit tests from a crawl, then run them with{" "}
            <code className="rounded bg-slate-800 px-1 text-slate-200">
              uaip run --trace ./out
            </code>
            . The trace will show up here automatically.
          </p>
        </div>
      )}

      {traces !== null && traces.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Run id</th>
                <th className="px-4 py-2 font-medium">Framework</th>
                <th className="px-4 py-2 font-medium">Runner</th>
                <th className="px-4 py-2 font-medium">Tests</th>
                <th className="px-4 py-2 font-medium">Duration</th>
                <th className="px-4 py-2 font-medium">Uploaded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-950/40">
              {traces.map((t) => (
                <tr key={t.runId} className="hover:bg-slate-900/60">
                  <td className="px-4 py-2">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/runs/${encodeURIComponent(t.runId)}/trace`}
                      className="font-mono text-xs text-indigo-300 hover:text-indigo-200"
                    >
                      {t.runId}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-slate-300">{t.framework}</td>
                  <td className="px-4 py-2 text-slate-300">{t.runner}</td>
                  <td className="px-4 py-2 tabular-nums text-slate-300">
                    {t.testCount}
                  </td>
                  <td className="px-4 py-2 tabular-nums text-slate-300">
                    {formatDuration(t.durationMs)}
                  </td>
                  <td className="px-4 py-2 text-slate-400">
                    {formatRelativeTime(t.uploadedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function StatusBadge({ status }: { status: string }): JSX.Element {
  const cls =
    status === "passed"
      ? "bg-emerald-900/40 text-emerald-300 border-emerald-700"
      : status === "failed"
        ? "bg-rose-900/40 text-rose-300 border-rose-700"
        : status === "errored"
          ? "bg-amber-900/40 text-amber-300 border-amber-700"
          : "bg-slate-800 text-slate-300 border-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  );
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m}m ${rem}s`;
}

export function formatRelativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return iso;
  const delta = Date.now() - ts;
  const abs = Math.abs(delta);
  const sec = Math.round(abs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
